import { print, type DocumentNode } from 'graphql'

const api = import.meta.env.VITE_API_ENDPOINT

type GraphQLResponse<T> = {
  data?: T
  errors?: Array<{ message?: string }>
}

let authToken: string | null = null

export function setGraphClientAuthToken(token?: string | null) {
  if (!token) return
  authToken = token
}

class GraphClient {
  async request<T>(query: string | DocumentNode, variables?: unknown): Promise<T> {
    const queryString = typeof query === 'string' ? query : print(query)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15_000)

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }

    if (!authToken && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const urlToken = params.get('token')
      if (urlToken) authToken = urlToken
    }

    const token =
      authToken ??
      (typeof window !== 'undefined' ? window.localStorage.getItem('@token') : null)
    if (token) headers['Authorization'] = `Bearer ${token}`

    try {
      const response = await fetch(api, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: queryString, variables }),
        signal: controller.signal,
      })

      const payload = (await response.json()) as GraphQLResponse<T>

      if (payload.errors?.length) {
        throw new Error(payload.errors[0]?.message ?? 'GraphQL error')
      }

      return payload.data as T
    } finally {
      clearTimeout(timeoutId)
    }
  }
}

export const graphClient = new GraphClient()

if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') ?? window.localStorage.getItem('@token')
  if (token) authToken = token
}
